import { http } from '@/lib/http';
import { http as atsHttp } from '@/lib/ats-https';

export interface MentionSuggestion {
  id: string;
  name: string;
  entity: string;
  /** Role / job title shown as the right-side label for people. */
  subtitle?: string;
  /** Avatar image for people; falls back to initials when absent. */
  avatarUrl?: string;
}

export interface FetchMentionsOptions {
  signal?: AbortSignal;
}

const CONTACT_LIMIT = 20;
const LIMIT = 5;

// ── Response shapes (minimal — only the fields we use) ───────────────────────

interface RawContact {
  id: number;
  name?: string;
  first_name?: string;
  last_name?: string;
  position?: string;
  image_url?: string;
}

interface RawJob {
  id: number;
  name?: string;
}

interface RawOutreach {
  id: number;
  title?: string;
  name?: string;
}

// /outreaches?page=1           → { data: RawOutreach[] }
// /outreaches/search?q=...     → { data: RawOutreach[] } | { outreaches: RawOutreach[] }
type OutreachListResponse = {
  data?: RawOutreach[];
  outreaches?: RawOutreach[];
};

interface RawUser {
  id: number;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  avatar_url?: string;
}

// ── Name helpers ─────────────────────────────────────────────────────────────

const contactName = (c: RawContact): string => {
  if (c.name?.trim()) return c.name.trim();
  return (
    `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || `Contact ${c.id}`
  );
};

const userName = (u: RawUser): string => {
  if (u.full_name?.trim()) return u.full_name.trim();
  const full = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
  return full || u.email || `User ${u.id}`;
};

// ── Fetcher ──────────────────────────────────────────────────────────────────

export const fetchMentions = async (
  query: string,
  { signal }: FetchMentionsOptions = {}
): Promise<MentionSuggestion[]> => {
  const [contactsRes, jobsRes, flowsRes, usersRes] = await Promise.allSettled([
    http.post(
      '/search/circles/contacts',
      {
        name: query,
        limit: CONTACT_LIMIT,
        offset: 0,
        extended_fields: true,
        sort_element: 'tst',
        sort_order: 'desc',
      },
      { signal }
    ) as Promise<{ data?: { contact?: RawContact[] } }>,

    atsHttp.get('/ats_jobs', {
      params: { view: 'card', limit: LIMIT, q: query },
      signal,
    }) as Promise<{ data?: { ats_jobs?: RawJob[] } }>,

    (query
      ? http.get('/outreaches/search', {
          params: { q: query, page: 1, items: LIMIT },
          signal,
        })
      : http.get('/outreaches', {
          params: { page: 1, sort_by: { created_at: 'DESC' } },
          signal,
        })) as Promise<OutreachListResponse>,

    http.get('/account/users/search', {
      params: { 'filter_by[status][]': 'active', filter_value: query },
      signal,
    }) as Promise<{ data?: { users?: RawUser[] } }>,
  ]);

  const results: MentionSuggestion[] = [];

  if (contactsRes.status === 'fulfilled') {
    (contactsRes.value?.data?.contact ?? [])
      .slice(0, CONTACT_LIMIT)
      .forEach((c) => {
        results.push({
          id: String(c.id),
          name: contactName(c),
          entity: 'contact',
          subtitle: c.position?.trim() || undefined,
          avatarUrl: c.image_url || undefined,
        });
      });
  }

  if (jobsRes.status === 'fulfilled') {
    (jobsRes.value?.data?.ats_jobs ?? []).slice(0, LIMIT).forEach((j) => {
      results.push({
        id: String(j.id),
        name: j.name ?? `Job ${j.id}`,
        entity: 'job',
      });
    });
  }

  if (flowsRes.status === 'fulfilled') {
    const flows = flowsRes.value?.data ?? flowsRes.value?.outreaches ?? [];
    (flows as RawOutreach[]).slice(0, LIMIT).forEach((f) => {
      results.push({
        id: String(f.id),
        name: f.title ?? f.name ?? `Flow ${f.id}`,
        entity: 'flow',
      });
    });
  }

  if (usersRes.status === 'fulfilled') {
    (usersRes.value?.data?.users ?? []).slice(0, LIMIT).forEach((u) => {
      results.push({
        id: String(u.id),
        name: userName(u),
        entity: 'user',
        avatarUrl: u.avatar_url || undefined,
      });
    });
  }

  return results;
};
